import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  FaChartLine, FaShoppingBag, FaDollarSign, FaUsers,
  FaFilter, FaFileDownload
} from 'react-icons/fa';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Button from '../../../components/general/button/Button';
import Swal from 'sweetalert2';
import '../../../assets/css/seller/analytics/SellerAnalytics.css';
import SellerLayout from '../../../layouts/SellerLayout';

const SellerAnalytics = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleExportReport = async () => {
    const { value: format } = await Swal.fire({
      title: 'Export Analytics Report',
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px;">Select export format:</p>
        </div>
      `,
      input: 'radio',
      inputOptions: {
        'pdf': 'PDF Document',
        'csv': 'CSV (Excel)',
        'excel': 'Excel Workbook'
      },
      inputValue: 'pdf',
      showCancelButton: true,
      confirmButtonColor: '#097d43',
      confirmButtonText: 'Export',
      inputValidator: (value) => {
        if (!value) {
          return 'Please select a format!';
        }
      }
    });

    if (format) {
      Swal.fire({
        title: 'Generating Report...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      setTimeout(() => {
        Swal.fire({
          title: 'Success!',
          html: `
            <p>Report exported successfully!</p>
            <p style="color: #6b7280; font-size: 0.875rem; margin-top: 10px;">
              Format: ${format.toUpperCase()}<br/>
              Period: ${dateRange === '7days' ? 'Last 7 Days' : dateRange === '30days' ? 'Last 30 Days' : 'Last 3 Months'}
            </p>
          `,
          icon: 'success',
          confirmButtonColor: '#097d43'
        });
      }, 2000);
    }
  };

  const stats = [
    { icon: <FaDollarSign />, color: 'blue', label: 'Total Revenue', value: 'PKR 89,450', change: '+12.5%' },
    { icon: <FaShoppingBag />, color: 'green', label: 'Total Orders', value: '456', change: '+8.2%' },
    { icon: <FaUsers />, color: 'orange', label: 'Customers', value: '234', change: '+15.3%' },
    { icon: <FaChartLine />, color: 'purple', label: 'Growth Rate', value: '23%', change: '+5.1%' }
  ];

  const revenueData = [
    { month: 'Jan', revenue: 45000, orders: 234 },
    { month: 'Feb', revenue: 52000, orders: 289 },
    { month: 'Mar', revenue: 48000, orders: 256 },
    { month: 'Apr', revenue: 65000, orders: 324 },
    { month: 'May', revenue: 71000, orders: 378 },
    { month: 'Jun', revenue: 89000, orders: 445 }
  ];

  const categoryData = [
    { name: 'Seeds', value: 35, color: '#3b82f6' },
    { name: 'Fertilizers', value: 30, color: '#22c55e' },
    { name: 'Tools', value: 20, color: '#f97316' },
    { name: 'Pesticides', value: 15, color: '#a855f7' }
  ];

  const weeklyData = [
    { day: 'Mon', sales: 12000 },
    { day: 'Tue', sales: 15000 },
    { day: 'Wed', sales: 18000 },
    { day: 'Thu', sales: 14000 },
    { day: 'Fri', sales: 22000 },
    { day: 'Sat', sales: 25000 },
    { day: 'Sun', sales: 20000 }
  ];

  const topProducts = [
    { rank: 1, name: 'Organic Potash Fertilizer', sales: 145, revenue: 'PKR 72,500' },
    { rank: 2, name: 'Hybrid Tomato F1 Seeds', sales: 128, revenue: 'PKR 51,200' },
    { rank: 3, name: 'Garden Trowel Set', sales: 98, revenue: 'PKR 39,200' },
    { rank: 4, name: 'Neem Oil Pesticide', sales: 87, revenue: 'PKR 34,800' },
    { rank: 5, name: 'Drip Irrigation Kit', sales: 76, revenue: 'PKR 76,000' }
  ];

  const performanceData = [
    { metric: 'Total Revenue', value: 'PKR 89,450', trend: '+12.5%', positive: true },
    { metric: 'Total Orders', value: '456', trend: '+8.2%', positive: true },
    { metric: 'Avg. Order Value', value: 'PKR 196', trend: '+4.3%', positive: true },
    { metric: 'Success Rate', value: '94.2%', trend: '+2.1%', positive: true }
  ];

  return (
    <SellerLayout>
    <div className="ana-container">
      <Container fluid>
        
        {/* Header */}
        <div className="ana-header">
          <div>
            <h1>Sales Analytics</h1>
            <p>Track your business performance</p>
          </div>
          <div className="ana-actions">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="ana-date-select"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
            </select>
            <Button variant="outline" icon={<FaFilter />} size="medium">
              Filter
            </Button>
            <Button variant="gradient" icon={<FaFileDownload />} size="medium" onClick={handleExportReport}>
              Export Report
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <Row className="ana-stats g-3 g-lg-4">
            {[1, 2, 3, 4].map((i) => (
              <Col key={i} xs={12} sm={6} lg={3}>
                <div className="ana-stat-card ana-skeleton">
                  <div className="ana-skeleton-icon"></div>
                  <div className="ana-skeleton-content">
                    <div className="ana-skeleton-line short"></div>
                    <div className="ana-skeleton-line medium"></div>
                    <div className="ana-skeleton-line small"></div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <Row className="ana-stats g-3 g-lg-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={3}>
                <div className="ana-stat-card">
                  <div className={`ana-stat-icon ${stat.color}`}>
                    {stat.icon}
                  </div>
                  <div className="ana-stat-content">
                    <div className="ana-stat-label">{stat.label}</div>
                    <div className="ana-stat-value">{stat.value}</div>
                    <div className="ana-stat-change">{stat.change}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* Charts Section */}
        <Row className="g-3 g-lg-4">
          {/* Revenue & Orders Chart */}
          <Col xs={12} lg={8}>
            {loading ? (
              <div className="ana-chart-card ana-skeleton">
                <div className="ana-skeleton-header">
                  <div className="ana-skeleton-line medium"></div>
                  <div className="ana-skeleton-line small"></div>
                </div>
                <div className="ana-skeleton-chart"></div>
              </div>
            ) : (
              <div className="ana-chart-card">
                <div className="ana-chart-header">
                  <h3>Revenue & Orders Overview</h3>
                  <p>Monthly performance trends</p>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis yAxisId="left" stroke="#6b7280" />
                    <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                    <Tooltip />
                    <Legend />
                    <Line 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#097d43" 
                      strokeWidth={3}
                      name="Revenue (PKR)"
                      dot={{ fill: '#097d43', r: 5 }}
                    />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="orders" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      name="Orders"
                      dot={{ fill: '#3b82f6', r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Col>

          {/* Category Distribution */}
          <Col xs={12} lg={4}>
            {loading ? (
              <div className="ana-chart-card ana-skeleton">
                <div className="ana-skeleton-header">
                  <div className="ana-skeleton-line medium"></div>
                  <div className="ana-skeleton-line small"></div>
                </div>
                <div className="ana-skeleton-pie"></div>
              </div>
            ) : (
              <div className="ana-chart-card">
                <div className="ana-chart-header">
                  <h3>Sales by Category</h3>
                  <p>Product distribution</p>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Col>
        </Row>

        {/* Weekly Sales & Top Products */}
        <Row className="g-3 g-lg-4">
          <Col xs={12} lg={7}>
            {loading ? (
              <div className="ana-chart-card ana-skeleton">
                <div className="ana-skeleton-header">
                  <div className="ana-skeleton-line medium"></div>
                  <div className="ana-skeleton-line small"></div>
                </div>
                <div className="ana-skeleton-bar"></div>
              </div>
            ) : (
              <div className="ana-chart-card">
                <div className="ana-chart-header">
                  <h3>Weekly Sales Trend</h3>
                  <p>Daily sales performance</p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip />
                    <Bar dataKey="sales" fill="#097d43" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Col>

          {/* Top Products */}
          <Col xs={12} lg={5}>
            {loading ? (
              <div className="ana-chart-card ana-skeleton">
                <div className="ana-skeleton-header">
                  <div className="ana-skeleton-line medium"></div>
                  <div className="ana-skeleton-line small"></div>
                </div>
                <div className="ana-skeleton-table">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="ana-skeleton-row"></div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ana-chart-card">
                <div className="ana-chart-header">
                  <h3>Top Selling Products</h3>
                  <p>Best performers this month</p>
                </div>
                <div className="ana-top-products">
                  <table className="ana-products-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>PRODUCT</th>
                        <th>SALES</th>
                        <th>REVENUE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((product) => (
                        <tr key={product.rank}>
                          <td className="ana-rank">{product.rank}</td>
                          <td className="ana-product-name">{product.name}</td>
                          <td>{product.sales}</td>
                          <td className="ana-revenue">{product.revenue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Performance Summary Table */}
        {loading ? (
          <div className="ana-summary-card ana-skeleton">
            <div className="ana-skeleton-header">
              <div className="ana-skeleton-line medium"></div>
            </div>
            <div className="ana-skeleton-table">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="ana-skeleton-row"></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="ana-summary-card">
            <div className="ana-summary-header">
              <h3>Performance Summary</h3>
              <Button variant="outline" size="small">View All</Button>
            </div>
            <div className="ana-summary-table-wrapper">
              <table className="ana-summary-table">
                <thead>
                  <tr>
                    <th>METRIC</th>
                    <th>VALUE</th>
                    <th>TREND</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.map((item, index) => (
                    <tr key={index}>
                      <td className="ana-metric">{item.metric}</td>
                      <td className="ana-value">{item.value}</td>
                      <td>
                        <span className={`ana-trend ${item.positive ? 'positive' : 'negative'}`}>
                          {item.positive ? '↑' : '↓'} {item.trend}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </Container>
    </div>
    </SellerLayout>
  );
};

export default SellerAnalytics;